import {
  IBudget,
  IBudgetRequest,
  IBudgetVersionService,
  IBudgetVersionServiceItem,
} from "../../../../models/budgetV2";

import { dedupeServiceItems } from "../../utils/dedupeServiceItems";

const roundMoney = (value: number) => Math.round((value + Number.EPSILON) * 100) / 100;

const getServiceItems = (service: IBudgetVersionService): IBudgetVersionServiceItem[] => {
  const items = service.items?.length ? service.items : service.serviceItems;
  return dedupeServiceItems((items ?? []).filter((item) => item.removed !== true));
};

const mapServiceItem = (item: IBudgetVersionServiceItem) => {
  const quantity = Number(item.quantity ?? 0);
  const value = Number(item.value ?? 0);
  const discountPercent = Number(item.discount ?? 0);
  const gross = quantity * value;
  const discountAbsolute = roundMoney((gross * discountPercent) / 100);
  const subtotal = item.isCovered === true ? 0 : roundMoney(gross - discountAbsolute);

  return {
    id: item.id,
    ref: item.ref,
    item: item.item,
    description: item.description,
    quantity,
    value,
    discount: discountAbsolute,
    subtotal,
    isCovered: item.isCovered === true,
    notCoveredReason: item.notCoveredReason,
    availableIn: item.availableIn,
  };
};

const mapService = (service: IBudgetVersionService) => {
  const serviceItems = getServiceItems(service).map(mapServiceItem);
  const serviceSubtotal = roundMoney(
    serviceItems.reduce((sum, item) => sum + (item.subtotal ?? 0), 0)
  );
  const parsedId = Number.parseInt(String(service.id ?? ""), 10);

  return {
    id: Number.isNaN(parsedId) ? undefined : parsedId,
    ref: service.ref,
    name: service.name,
    service: service.service ?? service.name,
    serviceType: service.serviceType,
    description: service.description,
    serviceItems,
    items: serviceItems,
    percentages: service.percentages,
    failureMode: service.failureMode,
    observation: service.observation,
    subtotal: serviceSubtotal,
    discount: service.discount ?? 0,
    isExternalService: service.isExternalService ?? false,
    code: service.code,
    subscriptionBalanceApplied: service.subscriptionBalanceApplied,
  };
};

export const buildBudgetRequestFromBudget = (
  budget: IBudget,
  occurrenceUuid?: string | null,
  proposedBudget?: IBudget | null
): IBudgetRequest => {
  const services = (budget.versionData?.services ?? []).map(mapService);
  // proposalServices is the history of the change: what was proposed (the services of the version being edited),
  // while `services` carries the new values. Without a proposed budget (create) it keeps what the budget holds.
  const proposalServices = (proposedBudget
    ? proposedBudget.versionData?.services
    : budget.versionData?.proposalServices ?? []
  )?.map(mapService) ?? [];
  const subscriptionBalanceToApply = budget.subscriptionBalanceApplied ?? 0;
  const subtotal = roundMoney(
    services.reduce((sum, service) => sum + (service.subtotal ?? 0), 0)
  );
  const discount = 0;
  const netTotal = roundMoney(subtotal - discount - subscriptionBalanceToApply);
  const parsedTruckerPayPlanId = Number.parseInt(String(budget.truckerPayPlanId ?? ""), 10);

  return {
    occurrenceProtocolNumber: budget.occurrenceProtocolNumber,
    occurrenceUuid: occurrenceUuid ?? null,
    consultantName: budget.consultantName,
    customer: {
      name: budget.customerName,
      email: budget.customerEmail,
      phone: budget.customerPhone,
    },
    vehicle: {
      chassis: budget.vehicleChassis,
      licensePlate: budget.vehicleLicensePlate,
      model: budget.vehicleModel,
      odometer: budget.vehicleOdometer,
      hourMeter: budget.vehicleHourMeter,
      contractNumber: budget.vehicleContractNumber,
    },
    proposalServices: proposalServices as IBudgetVersionService[],
    services: services as IBudgetVersionService[],
    subtotal,
    discount,
    netTotal,
    observation: budget.versionData?.observation,
    subscriptionBalanceToApply,
    truckerPayPlanId: Number.isNaN(parsedTruckerPayPlanId) ? null : parsedTruckerPayPlanId,
  };
};

export const getComparableBudgetSnapshot = (budget: IBudget | null) => {
  if (!budget) return null;

  return {
    services: budget.versionData?.services ?? [],
    proposalServices: budget.versionData?.proposalServices ?? [],
    observation: budget.versionData?.observation ?? "",
    counterProposalValue: budget.versionData?.counterProposalValue ?? null,
    counterProposalObservation: budget.versionData?.counterProposalObservation ?? "",
    subscriptionBalanceApplied: budget.subscriptionBalanceApplied ?? 0,
  };
};
